import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { LogOut, RefreshCw, FileText, ExternalLink, Check, X, Upload, MessageCircle } from "lucide-react";
import { toast } from "sonner";
import { useLang } from "../context/LanguageContext";
import { t, DEADLINES, RETAILER_SET_STATUSES } from "../lib/i18n";
import { partnerApi, loadPartnerToken, setPartnerToken, partnerFileUrl } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Badge } from "../components/ui/badge";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "../components/ui/select";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "../components/ui/dialog";

const STATUS_BADGE = {
  "Received":            "bg-slate-100 text-slate-800",
  "Documents Checking":  "bg-yellow-100 text-yellow-900",
  "In Process":          "bg-blue-100 text-blue-800",
  "Completed":           "bg-green-100 text-green-800",
  "Rejected":            "bg-red-100 text-red-800",
  "Transferred":         "bg-purple-100 text-purple-800",
  "Assigned":            "bg-indigo-100 text-indigo-800",
};

const PartnerDashboardPage = () => {
  const { tr } = useLang();
  const navigate = useNavigate();
  const [me, setMe] = useState(null);
  const [token, setToken] = useState(null);
  const [tab, setTab] = useState("available");
  const [leads, setLeads] = useState([]);
  const [selected, setSelected] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const tk = loadPartnerToken();
    if (!tk) { navigate("/partner/login"); return; }
    setToken(tk);
    const stored = localStorage.getItem("ap_partner");
    if (stored) setMe(JSON.parse(stored));
    fetchLeads(tab);
    // eslint-disable-next-line
  }, []);

  useEffect(() => { if (token) fetchLeads(tab); /* eslint-disable-next-line */ }, [tab]);

  const fetchLeads = async (bucket) => {
    setLoading(true);
    try {
      const res = await partnerApi.get("/retailer/leads", { params: { bucket } });
      setLeads(res.data || []);
    } catch (err) {
      if (err?.response?.status === 401) { setPartnerToken(null); navigate("/partner/login"); }
      else toast.error(err?.response?.data?.detail || "Failed to load");
    } finally {
      setLoading(false);
    }
  };

  const logout = () => {
    setPartnerToken(null);
    localStorage.removeItem("ap_partner");
    navigate("/partner/login");
  };

  return (
    <div data-testid="partner-dashboard" className="max-w-7xl mx-auto px-4 lg:px-8 py-8 lg:py-10">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-6">
        <div>
          <div className="text-xs uppercase tracking-[0.14em] text-teal-700 font-semibold">{tr(t.partner.dashboard)}</div>
          <h1 className="mt-1 text-2xl sm:text-3xl font-heading font-semibold text-slate-900">
            {me?.shop_name || "—"}
          </h1>
          <div className="text-sm text-slate-500">{me?.name} · {me?.mobile}</div>
        </div>
        <Button variant="outline" onClick={logout} data-testid="partner-logout-btn">
          <LogOut className="w-4 h-4 mr-2" /> {tr(t.admin.signout)}
        </Button>
      </div>

      <Tabs value={tab} onValueChange={setTab}>
        <div className="flex items-center justify-between mb-4 gap-3">
          <TabsList data-testid="partner-tabs">
            <TabsTrigger value="available" data-testid="tab-available">{tr({ en: "Available", hi: "उपलब्ध" })}</TabsTrigger>
            <TabsTrigger value="assigned" data-testid="tab-assigned">{tr({ en: "My Work", hi: "मेरा कार्य" })}</TabsTrigger>
            <TabsTrigger value="completed" data-testid="tab-completed">{tr(t.status.Completed)}</TabsTrigger>
          </TabsList>
          <Button variant="outline" size="sm" onClick={() => fetchLeads(tab)}>
            <RefreshCw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} />
          </Button>
        </div>

        {["available", "assigned", "completed"].map((b) => (
          <TabsContent key={b} value={b} className="mt-0">
            <LeadList leads={leads} onSelect={setSelected} loading={loading} tr={tr} />
          </TabsContent>
        ))}
      </Tabs>

      <PartnerLeadDialog
        lead={selected}
        token={token}
        onClose={() => setSelected(null)}
        onChange={() => fetchLeads(tab)}
        tr={tr}
      />
    </div>
  );
};

const LeadList = ({ leads, onSelect, loading, tr }) => (
  <div className="rounded-lg bg-white border border-slate-200 overflow-hidden">
    <div className="overflow-x-auto">
      <table className="w-full text-sm">
        <thead className="bg-slate-50 text-slate-600 text-left">
          <tr>
            <th className="px-4 py-3 font-medium">{tr(t.common.reqId)}</th>
            <th className="px-4 py-3 font-medium">{tr(t.common.service)}</th>
            <th className="px-4 py-3 font-medium">{tr(t.common.status)}</th>
            <th className="px-4 py-3 font-medium">{tr({ en: "Deadline", hi: "समय-सीमा" })}</th>
            <th className="px-4 py-3 font-medium">{tr(t.common.created)}</th>
            <th className="px-4 py-3"></th>
          </tr>
        </thead>
        <tbody>
          {loading && <tr><td colSpan="6" className="px-4 py-10 text-center text-slate-500">{tr({ en: "Loading…", hi: "लोड हो रहा है…" })}</td></tr>}
          {!loading && leads.length === 0 && <tr><td colSpan="6" className="px-4 py-10 text-center text-slate-500">{tr({ en: "Nothing here yet.", hi: "अभी कुछ नहीं।" })}</td></tr>}
          {!loading && leads.map((l) => (
            <tr key={l.id} className="border-t border-slate-100 hover:bg-slate-50/60 cursor-pointer" onClick={() => onSelect(l)} data-testid={`partner-row-${l.request_id}`}>
              <td className="px-4 py-3 font-medium text-slate-900">{l.request_id}</td>
              <td className="px-4 py-3">{l.service_name}{l.custom_work ? ` · ${l.custom_work}` : ""}</td>
              <td className="px-4 py-3"><Badge className={`${STATUS_BADGE[l.status]} rounded-full px-2.5 py-0.5`}>{tr(t.status[l.status])}</Badge></td>
              <td className="px-4 py-3 text-slate-700">{l.deadline || "—"}</td>
              <td className="px-4 py-3 text-slate-500 whitespace-nowrap">{new Date(l.created_at).toLocaleString()}</td>
              <td className="px-4 py-3 text-right"><span className="text-teal-700 text-xs font-medium">{tr({ en: "Open", hi: "खोलें" })}</span></td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  </div>
);

const PartnerLeadDialog = ({ lead, token, onClose, onChange, tr }) => {
  const [deadline, setDeadline] = useState("Same Day");
  const [customDeadline, setCustomDeadline] = useState("");
  const [rejectReason, setRejectReason] = useState("");
  const [statusChoice, setStatusChoice] = useState("In Process");
  const [note, setNote] = useState("");
  const [saving, setSaving] = useState(false);
  const fileRef = useRef(null);

  useEffect(() => {
    if (lead) {
      setStatusChoice(lead.status === "Completed" ? "Completed" : "In Process");
      setNote(lead.admin_note || "");
      setRejectReason("");
      setDeadline("Same Day");
      setCustomDeadline("");
    }
  }, [lead]);

  if (!lead) return null;

  const isTransferred = lead.status === "Transferred";
  const isAssigned = ["Assigned", "In Process"].includes(lead.status);
  const isDone = lead.status === "Completed";

  const accept = async () => {
    setSaving(true);
    try {
      await partnerApi.post(`/retailer/leads/${lead.id}/accept`, { deadline, custom_deadline: customDeadline });
      toast.success(tr({ en: "Accepted! Lead assigned to you.", hi: "स्वीकारा! आपको सौंपा गया।" }));
      onChange(); onClose();
    } catch (err) { toast.error(err?.response?.data?.detail || "Accept failed"); }
    finally { setSaving(false); }
  };

  const reject = async () => {
    if (!rejectReason.trim()) { toast.error(tr({ en: "Please enter a reason", hi: "कारण दर्ज करें" })); return; }
    setSaving(true);
    try {
      await partnerApi.post(`/retailer/leads/${lead.id}/reject`, { reason: rejectReason });
      toast.success(tr({ en: "Rejected", hi: "अस्वीकृत" }));
      onChange(); onClose();
    } catch (err) { toast.error(err?.response?.data?.detail || "Reject failed"); }
    finally { setSaving(false); }
  };

  const updateStatus = async () => {
    setSaving(true);
    try {
      await partnerApi.patch(`/retailer/leads/${lead.id}/status`, { status: statusChoice, admin_note: note });
      toast.success(tr({ en: "Updated", hi: "अपडेट हुआ" }));
      onChange(); onClose();
    } catch (err) { toast.error(err?.response?.data?.detail || "Update failed"); }
    finally { setSaving(false); }
  };

  const uploadReceipt = async (files) => {
    if (!files || files.length === 0) return;
    setSaving(true);
    try {
      const fd = new FormData();
      Array.from(files).forEach((f) => fd.append("files", f));
      await partnerApi.post(`/retailer/leads/${lead.id}/upload`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(tr({ en: "Receipt uploaded", hi: "रसीद अपलोड" }));
      onChange();
    } catch (err) { toast.error(err?.response?.data?.detail || "Upload failed"); }
    finally { setSaving(false); }
  };

  const waMsg = `Namaste ${lead.name}, aapka Ashish Point request ${lead.request_id} (${lead.service_name}) ka kaam ${isDone ? "COMPLETE ho gaya hai" : "process me hai"}. — ${lead.deadline || ""}`;

  return (
    <Dialog open={!!lead} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="partner-lead-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading">{lead.request_id} — {lead.name}</DialogTitle>
          <DialogDescription>{lead.service_name}{lead.custom_work ? ` · ${lead.custom_work}` : ""}</DialogDescription>
        </DialogHeader>

        <div className="space-y-4 text-sm">
          <div className="grid grid-cols-2 gap-3">
            <Info label={tr(t.common.mobile)} value={
              <a href={`tel:${lead.mobile}`} className="text-teal-700 hover:underline">{lead.mobile}</a>
            } />
            <Info label={tr(t.common.email)} value={lead.email || "—"} />
            <Info className="col-span-2" label={tr(t.common.address)} value={lead.address} />
            {lead.notes && <Info className="col-span-2" label={tr(t.common.notes)} value={lead.notes} />}
            {lead.deadline && <Info label={tr({ en: "Deadline", hi: "समय-सीमा" })} value={lead.deadline} />}
          </div>

          {/* Documents — only visible when assigned to this partner */}
          {(isAssigned || isDone) && (
            <div>
              <div className="text-xs uppercase tracking-widest text-slate-500 font-semibold mb-2">{tr({ en: "Customer Documents", hi: "ग्राहक के दस्तावेज़" })}</div>
              <ul className="space-y-2">
                {(lead.files || []).map((f) => (
                  <li key={f.file_id} className="flex items-center justify-between rounded-md border border-slate-200 px-3 py-2">
                    <div className="flex items-center gap-2 min-w-0">
                      <FileText className="w-4 h-4 text-teal-700 shrink-0" />
                      <span className="text-sm text-slate-800 truncate">{f.original_filename}</span>
                    </div>
                    <a href={partnerFileUrl(f.file_id, token)} target="_blank" rel="noopener noreferrer" className="text-teal-700 hover:underline text-xs font-medium inline-flex items-center gap-1">
                      {tr({ en: "Download", hi: "डाउनलोड" })} <ExternalLink className="w-3 h-3" />
                    </a>
                  </li>
                ))}
                {(!lead.files || lead.files.length === 0) && <li className="text-slate-500 text-sm">—</li>}
              </ul>
            </div>
          )}

          {/* Actions per state */}
          {isTransferred && (
            <div className="rounded-lg border border-teal-100 bg-teal-50/40 p-4 space-y-4">
              <div className="font-medium text-slate-900">{tr(t.partner.deadline)}</div>
              <div className="grid grid-cols-2 gap-3">
                <Select value={deadline} onValueChange={setDeadline}>
                  <SelectTrigger data-testid="partner-deadline-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {DEADLINES.map((d) => <SelectItem key={d} value={d}>{tr(t.deadline[d])}</SelectItem>)}
                  </SelectContent>
                </Select>
                {deadline === "Custom" && (
                  <Input value={customDeadline} onChange={(e) => setCustomDeadline(e.target.value)} placeholder="e.g. 3 days" data-testid="partner-custom-deadline" />
                )}
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 pt-2">
                <Button onClick={accept} disabled={saving} className="bg-green-600 hover:bg-green-700 text-white" data-testid="partner-accept-btn">
                  <Check className="w-4 h-4 mr-2" /> {tr(t.partner.accept)}
                </Button>
                <div className="space-y-2">
                  <Input value={rejectReason} onChange={(e) => setRejectReason(e.target.value)} placeholder={tr(t.partner.reason)} data-testid="partner-reject-reason" />
                  <Button onClick={reject} disabled={saving} variant="destructive" className="w-full" data-testid="partner-reject-btn">
                    <X className="w-4 h-4 mr-2" /> {tr(t.partner.reject)}
                  </Button>
                </div>
              </div>
            </div>
          )}

          {isAssigned && (
            <div className="rounded-lg border border-slate-200 p-4 space-y-3">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                  <div className="text-xs uppercase tracking-widest text-slate-500 font-semibold mb-2">{tr(t.common.status)}</div>
                  <Select value={statusChoice} onValueChange={setStatusChoice}>
                    <SelectTrigger data-testid="partner-status-select"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {RETAILER_SET_STATUSES.map((s) => <SelectItem key={s} value={s}>{tr(t.status[s])}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <div className="text-xs uppercase tracking-widest text-slate-500 font-semibold mb-2">{tr({ en: "Note", hi: "नोट" })}</div>
                  <Textarea rows={2} value={note} onChange={(e) => setNote(e.target.value)} data-testid="partner-note" />
                </div>
              </div>

              <div>
                <Button type="button" variant="outline" onClick={() => fileRef.current?.click()} className="w-full" data-testid="partner-upload-btn">
                  <Upload className="w-4 h-4 mr-2" /> {tr(t.partner.receipt)}
                </Button>
                <input ref={fileRef} type="file" multiple accept=".jpg,.jpeg,.png,.pdf" className="hidden" onChange={(e) => uploadReceipt(e.target.files)} />
              </div>

              {(lead.receipt_files && lead.receipt_files.length > 0) && (
                <ul className="space-y-1">
                  {lead.receipt_files.map((f) => (
                    <li key={f.file_id} className="text-xs text-slate-600 flex items-center gap-1">
                      <FileText className="w-3 h-3" /> {f.original_filename}
                    </li>
                  ))}
                </ul>
              )}

              <Button onClick={updateStatus} disabled={saving} className="w-full bg-teal-700 hover:bg-teal-800 text-white" data-testid="partner-save-status">
                {tr({ en: "Save Status", hi: "स्थिति सहेजें" })}
              </Button>

              <a
                href={`https://wa.me/${lead.mobile.replace(/[^0-9]/g, "")}?text=${encodeURIComponent(waMsg)}`}
                target="_blank" rel="noopener noreferrer"
                className="inline-flex w-full items-center justify-center gap-2 h-10 rounded-md bg-green-600 hover:bg-green-700 text-white text-sm font-medium"
                data-testid="partner-notify-customer"
              >
                <MessageCircle className="w-4 h-4" /> {tr({ en: "WhatsApp Customer", hi: "ग्राहक को WhatsApp" })}
              </a>
            </div>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>{tr({ en: "Close", hi: "बंद करें" })}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

const Info = ({ label, value, className = "" }) => (
  <div className={className}>
    <div className="text-xs uppercase tracking-widest text-slate-500 font-medium">{label}</div>
    <div className="mt-1 text-slate-800 whitespace-pre-wrap">{value}</div>
  </div>
);

export default PartnerDashboardPage;
